import { useViewListContext } from "@/context/ViewListContext";
import { LangKey, useTranslation } from "@vixi/localization";
import { CreateAvatarFormValues, CustomPreferenceOption } from "@/routes/_app/avatars/create";
import { SimplePrimitive } from "@vixi/types";
import { ComponentPropsWithoutRef, forwardRef, useCallback } from "react";
import { FieldName, useFormContext } from "react-hook-form";
import { Button, Divider, FieldLabel, Img, TextInput } from "@vixi/ui";
import clsx from "clsx";

const OrDivider = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>((props, ref) => {
  const { t } = useTranslation();

  return (
    <div ref={ref} {...props} className="mx-auto flex w-257 max-w-full items-center gap-7.75">
      <Divider thickness={1} />
      <p className="capitalize text-[24px] font-semibold">{t("orCap")}</p>
      <Divider thickness={1} />
    </div>
  );
});

// Helpers
const valueIsValid = (value: SimplePrimitive) => {
  if (value === undefined || value === null) {
    return false;
  }
  if (typeof value === "string" && value.trim().length < 3) {
    return false;
  }
  if (typeof value === "number" && isNaN(value)) {
    return false;
  }
  return true;
};

interface CustomPreferenceStepProps<FieldType extends string> {
  text: {
    header: LangKey;
    placeholder: LangKey;
    cta: LangKey;
    nextBtnText: LangKey;
    useAvatar: LangKey;
  };
  onNext: () => void;
  field: FieldType;
  options?: CustomPreferenceOption[];
}

/**
 * TODO: Fix all the "as any" usages in this file.
 * @param props.text - Dictionary keys of text to display in the step (header, placeholder, cta, nextText)
 * @param field - The field key that this step is responsible for (e.g., "outfit", "pose", "style")
 * @param options - The list of avatar options to display in the step
 */
export const CustomPreferenceStep = <FieldType extends FieldName<CreateAvatarFormValues>>({
  text,
  field,
  options,
}: CustomPreferenceStepProps<FieldType>) => {
  const { t } = useTranslation();
  const { register, setValue, watch } = useFormContext<CreateAvatarFormValues>();
  const viewList = useViewListContext();

  const onPreferenceSelected = useCallback(
    (value: SimplePrimitive) => {
      setValue(field as keyof CreateAvatarFormValues, t(value as any));
      // viewList?.nextStep();
    },
    [viewList, field],
  );

  return (
    <>
    <div className="flex flex-col gap-2.5 justify-center items-center">
        <FieldLabel className="text-[24px] font-semibold">{t(text.header)}</FieldLabel>
        <TextInput className="mt-2.5 w-[50%] " {...register(field as any)} placeholder={t(text.placeholder)} />
        <OrDivider  />
        <FieldLabel className="mb-3.5 text-[24px] font-semibold
         ">{t(text.cta)}</FieldLabel>

    </div>
    
      <div className="mx-auto mt-2.5 grid max-w-244.5 grid-cols-12 gap-x-18.75 gap-y-6.75">
        {options?.map((option, i) => (
          <div key={i} className="col-span-12 sm:col-span-6 lg:col-span-3">
            <Button
              onClick={() => onPreferenceSelected(option.value)}
              variant={"card"}
              className={clsx("h-100.5 w-full flex-col py-3.75", {
                "border-primary": t(option.value as any) === watch(field as any),
              })}
            >
              <p className="fs-13 mt-auto max-w-full px-5 whitespace-normal">{t(option.label)}</p>

              <div className="mt-auto mb-2.25 h-fit w-full basis-auto px-5">
                <Divider thickness={1} />
              </div>

              <Img contain src={option.image} className="h-67.75 w-full" />
            </Button>
          </div>
        ))}
      </div>

      {valueIsValid(watch(field as any)) && (
        <Button
          className="absolute end-20 bottom-12.5"
          variant="contained"
          size="large"
          onClick={viewList?.nextStep}
        >
          {t(text.nextBtnText)}
        </Button>
      )}
    </>
  );
};
